import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import * as moment from 'moment';
import { Alert } from '../../../../../../models/alerts/dto/alert';
import { AlertTypeEnum } from '../../../../../../models/alerts/enum/alert-type.enum';
import { PortalTypeIdEnum } from '../../../../../../models/enum/shared/portal-type-id.enum';
import { isoDateStringValidator } from '../../../../../../validators/iso-date-string-validator';

export const START_END_DATE_ERROR_KEY = 'validStartEndDate';

export type Meridiem = 'AM' | 'PM';

export type AlertDetailsForm = FormGroup<{
  typeId: FormControl<AlertTypeEnum>;
  colourId: FormControl<number | null>;
  dismissable: FormControl<boolean>;
  includeButton: FormControl<boolean>;
  title: FormControl<string | null>;
  description: FormControl<string | null>;
  buttonText: FormControl<string | null>;
  buttonLink: FormControl<string | null>;
  startDateString: FormControl<string | null>;
  startTime: FormControl<string | null>;
  endDateString: FormControl<string | null>;
  endTime: FormControl<string | null>;
  portalTypeId: FormControl<PortalTypeIdEnum | null>;
}>;

export function startEndDateValidator(): ValidatorFn {
  return group => {
    const startDate = group.get('startDateString')?.value;
    const startTime = group.get('startTime')?.value;
    const endDate = group.get('endDateString')?.value;
    const endTime = group.get('endTime')?.value;
    if (!startDate || !startTime || !endDate || !endTime) return null;
    const start = moment(`${startDate} ${startTime}`, 'YYYY-MM-DD HH:mm', true);
    const end = moment(`${endDate} ${endTime}`, 'YYYY-MM-DD HH:mm', true);
    if (!start.isValid() || !end.isValid()) return null;
    return end.isAfter(start) ? null : { [START_END_DATE_ERROR_KEY]: true };
  };
}

export function buildAlertDetailsForm(alert: Alert | null): AlertDetailsForm {
  return new FormGroup(
    {
      typeId: new FormControl<AlertTypeEnum>(alert?.typeId ?? AlertTypeEnum.Card, { nonNullable: true }),
      colourId: new FormControl<number | null>(alert?.colourId ?? null, { validators: [Validators.required] }),
      dismissable: new FormControl<boolean>(alert?.dismissable ?? false, { nonNullable: true }),
      includeButton: new FormControl<boolean>(alert?.includeButton ?? true, { nonNullable: true }),
      title: new FormControl<string | null>(alert?.title ?? null, { validators: [Validators.required] }),
      description: new FormControl<string | null>(alert?.description ?? null, { validators: [Validators.required] }),
      buttonText: new FormControl<string | null>(alert?.buttonText ?? null),
      buttonLink: new FormControl<string | null>(alert?.buttonLink ?? null),
      startDateString: new FormControl<string | null>(toNativeDateString(alert?.startDateString), {
        validators: [Validators.required, isoDateStringValidator]
      }),
      startTime: new FormControl<string | null>(alert?.startTime || null, { validators: [Validators.required] }),
      endDateString: new FormControl<string | null>(toNativeDateString(alert?.endDateString), {
        validators: [Validators.required, isoDateStringValidator]
      }),
      endTime: new FormControl<string | null>(alert?.endTime || null, { validators: [Validators.required] }),
      portalTypeId: new FormControl<PortalTypeIdEnum | null>(alert?.portalTypeId ?? null, {
        validators: [Validators.required]
      })
    },
    { validators: [startEndDateValidator()] }
  );
}

export function toNativeDateString(ddmmyyyy: string | null | undefined): string | null {
  if (!ddmmyyyy) return null;
  const parsed = moment(ddmmyyyy, 'DD-MM-YYYY', true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

export function splitTime24h(value: string | null): { time12h: string; meridiem: Meridiem } {
  if (!value) return { time12h: '', meridiem: 'AM' };
  const [hStr, mStr] = value.split(':');
  const h24 = Number(hStr);
  if (!Number.isFinite(h24)) return { time12h: '', meridiem: 'AM' };
  const meridiem: Meridiem = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return { time12h: `${pad2(h12)}:${mStr ?? '00'}`, meridiem };
}

export function combineTo24h(time12h: string, meridiem: Meridiem): string | null {
  if (!time12h || time12h.length < 5) return null;
  const [hStr, mStr] = time12h.split(':');
  const h12 = Number(hStr);
  const minutes = Number(mStr);
  if (!Number.isFinite(h12) || !Number.isFinite(minutes)) return null;
  if (h12 < 1 || h12 > 12 || minutes < 0 || minutes > 59) return null;
  const baseHour = h12 % 12;
  const h24 = meridiem === 'PM' ? baseHour + 12 : baseHour;
  return `${pad2(h24)}:${pad2(minutes)}`;
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}
